import {
  CLUSTER_PALETTE,
  clusterFromAccountHealth,
  type PortfolioClusterKey,
} from "./portfolioClusterStyles";
import type {
  BhClass,
  PortfolioMapAccount,
} from "../../Store/Reducers/PortfolioMapSlice";
import type {
  AccountHealthThresholds,
  BusinessHealthThresholds,
} from "../../Utils/HealthThresholds";
import {
  BUSINESS_HEALTH_COLORS,
  BUSINESS_HEALTH_SCORE_PALETTE,
  businessHealthClass,
} from "../../Utils/Helper/businessHealth";

/** PortfolioMapAccount joined (client-side, by account_id) with fields from
 * my-portfolio/overview — `al_level` and List Business Health aren't on
 * `GET /api/v1/portfolio-map` (or may lag). Computed once in PortfolioMapTab.tsx
 * and threaded through the scatter, matrix, and comparison-panel components
 * so they all share one join. `ah_cluster` is derived with vendor AH thresholds
 * at enrich time so matrix classify stays aligned with Settings bands. */
export interface EnrichedMapAccount extends PortfolioMapAccount {
  al: number | null;
  ah_cluster?: PortfolioClusterKey;
}

/** Same bands as List / Settings health_score_thresholds.business_health. */
export function bhClassFromScore(
  score: number | null | undefined,
  thresholds?: Partial<BusinessHealthThresholds> | null
): BhClass {
  const band = businessHealthClass(score, thresholds);
  if (band == null) return "no_data";
  if (band === "Strong") return "strong";
  if (band === "Stable") return "stable";
  return "concern";
}

/** Mock's pmDrawScatter `xTicks`/`yTicks` params are always 5 evenly-spaced
 * values from min to max (e.g. [0,25,50,75,100], [-100,-50,0,50,100] —
 * mock lines 5918/5939/5958). Derived generically from lens min/max rather
 * than hardcoded per-lens arrays, since every lens in this app follows that
 * same even-5-step pattern. */
export function evenTicks(min: number, max: number, count = 5): number[] {
  const step = (max - min) / (count - 1);
  return Array.from({ length: count }, (_, i) => Math.round(min + step * i));
}

const TIER_ORDER_LABEL: [string, string][] = [
  ["critical", "critical"],
  ["warning", "warning"],
  ["opp", "opportunity"],
  ["maint", "maintenance"],
];

export function tierBreakdown(
  tierCounts: Partial<Record<string, number>>
): string {
  return TIER_ORDER_LABEL.filter(([key]) => (tierCounts[key] ?? 0) > 0)
    .map(([key, label]) => `${tierCounts[key]} ${label}`)
    .join(", ");
}

/** Mock `getCompactSignalSelection` (v4.html ~6968-6976): Portfolio Map
 * right-panel rail shows one card per urgency tier when multiple tiers
 * fire; if only one tier is present, up to 3 cards from that tier. */
const COMPACT_TIER_ORDER = ["critical", "warning", "opp", "maint"] as const;

export function getCompactSignalSelection<T extends { tier: string }>(
  sorted: T[]
): { shown: T[]; tierCount: number; hiddenCount: number } {
  const groups = COMPACT_TIER_ORDER.map(tier => ({
    tier,
    signals: sorted.filter(signal => signal.tier === tier),
  })).filter(group => group.signals.length > 0);
  const shown =
    groups.length === 1
      ? groups[0].signals.slice(0, 3)
      : groups.map(group => group.signals[0]);
  return {
    shown,
    tierCount: groups.length,
    hiddenCount: Math.max(0, sorted.length - shown.length),
  };
}

export const RL_PRIORITY_LABEL: Record<string, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
  minimal: "Minimal",
  defer: "Defer",
};

// Dot stroke (border) + fill per BH class — same colors as List Business Health.
export const BH_CLASS_STROKE: Record<string, string> = {
  strong: BUSINESS_HEALTH_COLORS.strong,
  stable: BUSINESS_HEALTH_COLORS.stable,
  concern: BUSINESS_HEALTH_COLORS.concern,
  no_data: "#94a3b8",
};
export const BH_CLASS_FILL: Record<string, string> = {
  strong: BUSINESS_HEALTH_SCORE_PALETTE.strong.outer,
  stable: BUSINESS_HEALTH_SCORE_PALETTE.stable.outer,
  concern: BUSINESS_HEALTH_SCORE_PALETTE.concern.outer,
  no_data: "#f1f5f9",
};

// PM_DOT_R (mock line 5546) — exact per-tier radius, "Size = urgency".
export const TIER_RADIUS: Record<string, number> = {
  critical: 12,
  warning: 10,
  opp: 8,
  maint: 7,
};
/** Per-account when signals are on but this account has no active firing. */
export const NO_SIGNAL_RADIUS = 7;
/** Uniform size when Signal service / Open Actions is off for the org —
 * a bit larger than NO_SIGNAL_RADIUS so initials stay readable. */
export const SIGNALS_OFF_RADIUS = 10;

// Halo ring color per tier (mock 5823/6135) — critical/warning only.
export const TIER_HALO: Record<string, string> = {
  critical: "#c93c3c",
  warning: "#d9831f",
};

export function dotRadius(
  a: PortfolioMapAccount,
  opts?: { signalsVisible?: boolean }
): number {
  if (opts?.signalsVisible === false) return SIGNALS_OFF_RADIUS;
  if (!a.top_signal_tier) return NO_SIGNAL_RADIUS;
  return TIER_RADIUS[a.top_signal_tier] ?? NO_SIGNAL_RADIUS;
}

/** buildMAAccounts()'s initials derivation (mock ~7226): first letter of
 * each word, uppercased, max 2 chars. */
export function accountInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map(w => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

/** Drag distance (client px) below which a pointerdown+up on empty plot
 * space is treated as a click (clear selection), not a marquee drag —
 * mirrors PortfolioBirdsEyeMap.tsx's MARQUEE_MIN_DRAG_CLIENT_PX. */
export const MARQUEE_MIN_DRAG_CLIENT_PX = 5;

export function normalizeIds(ids: string[]): string[] {
  return Array.from(new Set(ids.filter(Boolean)));
}

/** Marquee (drag) selection: circle–rectangle intersection for dot hit-testing.
 * Ported verbatim from PortfolioBirdsEyeMap.tsx. */
export function circleIntersectsRect(
  cx: number,
  cy: number,
  r: number,
  rx: number,
  ry: number,
  rw: number,
  rh: number
): boolean {
  const nx = Math.max(rx, Math.min(cx, rx + rw));
  const ny = Math.max(ry, Math.min(cy, ry + rh));
  const dx = cx - nx;
  const dy = cy - ny;
  return dx * dx + dy * dy <= r * r;
}

/** Ported verbatim from PortfolioBirdsEyeMap.tsx. */
export function clientToSvgPoint(
  svg: SVGSVGElement,
  clientX: number,
  clientY: number
): { x: number; y: number } {
  const ctm = svg.getScreenCTM();
  if (ctm) {
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const sp = pt.matrixTransform(ctm.inverse());
    return { x: sp.x, y: sp.y };
  }
  const rect = svg.getBoundingClientRect();
  const vb = svg.viewBox.baseVal;
  const sx = vb.width / Math.max(rect.width, 1);
  const sy = vb.height / Math.max(rect.height, 1);
  return {
    x: (clientX - rect.left) * sx + vb.x,
    y: (clientY - rect.top) * sy + vb.y,
  };
}

/** Small deterministic per-account jitter (mock's cluster scatter uses
 * array-index-based jitter, `((i*17+7)%20-10)`; a hash of account_id is
 * used here instead so bubble positions stay stable across re-renders/
 * filtering rather than shifting when the account list order changes). */
export function jitterFromId(id: string, spread = 10): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) % 1000;
  }
  return (hash % (spread * 2)) - spread;
}

/** AH posture (Triage/Defend/Monitor/Expand) using vendor AH thresholds
 * (or product defaults). Prefer `account.ah_cluster` when already enriched. */
export function ahPostureLabel(
  ah: number | null,
  thresholds?: Partial<AccountHealthThresholds> | null,
  precomputed?: PortfolioClusterKey | null
): string {
  const cluster =
    precomputed ?? clusterFromAccountHealth(ah, undefined, thresholds);
  return cluster === "unscored" ? "Unscored" : CLUSTER_PALETTE[cluster].label;
}

/** Risk / Opp for hover cards. Prefer public-data scores (Net Investment);
 * fall back to KPI scores (AH Risk × Opportunity). */
export function displayRiskOpp(account: PortfolioMapAccount): {
  risk: number | null;
  opportunity: number | null;
} {
  return {
    risk: account.public_risk ?? account.risk,
    opportunity: account.public_opportunity ?? account.opportunity,
  };
}

export function formatRiskOppParts(account: PortfolioMapAccount): string[] {
  const { risk, opportunity } = displayRiskOpp(account);
  return [
    `Risk ${risk != null ? Math.round(risk) : "—"}`,
    `Opp ${opportunity != null ? Math.round(opportunity) : "—"}`,
  ];
}
